import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { MySQLDateToISOPipe } from 'app/shared/mysql-date-to-iso.pipe';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';
import { UIModalComponent } from 'app/ui/ui-modal.component';
import { ModalQuoteChooseProjectComponent } from '../modal-quote-choose-project/modal-quote-choose-project.component';
import { sendDealProjectChange } from 'app/quote/quote-deal-project';

declare const Mangler: any;

@Component({
	selector: 'modal-quote-mark-as-sent',
	templateUrl: './modal-quote-mark-as-sent.component.html'
})
export class ModalQuoteMarkAsSentComponent extends UIModalBaseComponent {

	@ViewChild('viewLines') viewLinesModal: UIModalComponent;
	@ViewChild('viewStock') viewStockModal: UIModalComponent;
	@ViewChild(ModalQuoteChooseProjectComponent) chooseProjectModal: ModalQuoteChooseProjectComponent;

	disabled = false;
	issues = null;
	issueTitle = '';
	data = null;
	quota;

	// What open() was called with, so a send held at the checkpoint can be resumed.
	reopenWith = null;

	disableOldProposals = false;
	redirectOldProposals = false;
	confirmed = {};

	record = {
		id: 0,
		quote_date: null,
		expiry_date: null,
		accepted_date: null,
		show_previous_proposals: false
	};

	lineIssues = ['zero_cost', 'zero_price', 'zero_margin', 'negative_margin', 'special_order_items', 'negative_labour_hour_margin', 'insufficient_stock'];
	type;
	constructor(
		public app: AppService,
		private api: ApiService,
		private router: Router
	) {
		super();
	}

	open(mdata: any) {
		// Kept so the checkpoint can reopen this exactly as it was opened (CRM-143).
		this.reopenWith = mdata;
		this.disableOldProposals = !!this.app.orgInfo?.disable_old_proposals;
		this.redirectOldProposals = !!this.app.orgInfo?.redirect_old_proposals;

		this.api.quote.getDefaultExpiryDate(mdata.quoteId, data => {
			this.record = data;
			this.record.quote_date = MySQLDateToISOPipe.stringToDate(this.record.quote_date);
			this.record.expiry_date = MySQLDateToISOPipe.stringToDate(this.record.expiry_date);

			this.loadIssues(() => {
				if (this.app.routeData.module === 'quote') {
					this.modal.open();
				} else {
					this.api.quote.downloadQuota(this.record.id, quota => {
						this.quota = quota;
						this.record.accepted_date = MySQLDateToISOPipe.stringToDate(quota.today);
						this.modal.open();
					}, error => {
						if (error.code === 1) {
							// Cannot accept quote, pop message asking them to upgrade their account.
							const buttons = ['Close'];
							if (this.app.orgInfo.role.manage_billing) buttons.push('*Go to Billing Settings');

							this.app.dialog.pop(this.app.routeData.module === 'sales-order' ? 'Finalise Sales Order' : 'Accept Quote', error.message, buttons, button => {
								if (button === 1) {
									this.router.navigate(['/' + this.app.orgInfo.alias, 'settings', 'billing']);
								}
							});
						} else {
							this.app.notifications.showDanger(error.message);
						}
					});
				}
			});
		}, error => {
			this.app.notifications.showDanger(error.message);
		});
	}

	/**
	 * Pulled out of open() so the dialog can re-run the checks in place: setting a project from the
	 * warning has to make that warning go away, and only the backend knows whether it actually did.
	 */
	loadIssues(done: () => void = null, preserveConfirmed = false) {
		const context = this.app.routeData.module === 'sales-order' ? 'completed' : 'sent';
		this.api.quote.issues(this.record.id, context, issueData => {
			this.issues = issueData;
			this.seedConfirmed(preserveConfirmed);
			if (done) done();
		}, error => {
			this.app.notifications.showDanger(error.message);
		});
	}

	seedConfirmed(preserve = false) {
		Mangler.each(this.issues.warnings, (k, v) => {
			// A tick is the user's assertion - a re-fetch has no standing to revoke it.
			if (preserve && this.confirmed[k]) return;
			this.confirmed[k] = this.lineIssues.includes(k) ? !v.length : !v;
		});

		// The project block is the only new warning that gates. Assigned on every seed, not just when
		// there are reasons: confirmed is never cleared between opens, so a stale false would disable
		// the button on the next quote. no_payment_stages is deliberately absent - it is a suggestion,
		// and anything landing in confirmed blocks the button.
		if (!(preserve && this.confirmed['project_required'])) {
			this.confirmed['project_required'] = !this.issues.project_required?.length;
		}
	}

	selectProject() {
		this.chooseProjectModal.open({
			customerId: this.issues?.quote_info?.customer_id,
			currencyId: this.issues?.quote_info?.currency_id,
			title: 'Add this quote to a project',
			message: 'This proposal needs a project before it is sent, so invoicing has somewhere to go.'
		});
	}

	chooseProjectModalClosed(projectId) {
		if (!projectId) return;

		this.disabled = true;
		sendDealProjectChange(this.app, this.api.quote.setProject, { id: this.record.id, project_id: projectId }, () => {
			this.app.notifications.showSuccess('Project set.');
			this.disabled = false;
			this.app.quoteStageChange.emit();
			// The backend decides whether the warning is gone, not us. Ticks already given are kept.
			this.loadIssues(null, true);
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		}, () => {
			this.disabled = false;
		});
	}

	markAsSentButton() {
		this.disabled = true;

		const data = Mangler.clone(this.record);
		data.quote_date = MySQLDateToISOPipe.dateToString(data.quote_date);
		data.expiry_date = MySQLDateToISOPipe.dateToString(data.expiry_date);
		// The org defaults pre-tick these, so they must not apply while the option is hidden.
		const disableOld = this.record.show_previous_proposals && this.disableOldProposals;
		data.disable_old_proposals = disableOld ? 1 : 0;
		data.redirect_old_proposals = disableOld && this.redirectOldProposals ? 1 : 0;

		this.api.quote.markAsSent(data, () => {
			this.disabled = false;
			this.app.quotePriceChange.emit();
			this.modal.close(true);
		}, error => {
			this.disabled = false;

			// Required work outstanding on the deal (CRM-143). This screen is itself a modal, so
			// the checkpoint is the global one and this closes first — two stacked dialogs, the
			// lower of which is asking about a different record, is not a thing to put in front of
			// somebody. Continuing then finishes this send, with what was confirmed here, rather
			// than asking for it a second time (BUG-11).
			if (this.app.requiredWork.hold(error, () => this.finishSend(data))) {
				this.modal.close();
				return;
			}

			this.app.notifications.showDanger(error.message);
		});
	}

	/**
	 * The send the checkpoint interrupted, after Continue. The dialog is already closed, so its
	 * result goes out the way a close would have sent it, and the screen that opened it carries on
	 * exactly as if the first confirm had gone through. Another checkpoint asks again; anything
	 * else reopens the dialog, so whatever is wrong can be put right there.
	 */
	private finishSend(data) {
		this.api.quote.markAsSent(data, () => {
			this.app.quotePriceChange.emit();
			this.close.emit(true);
		}, error => {
			if (this.app.requiredWork.hold(error, () => this.finishSend(data))) return;
			this.app.notifications.showDanger(error.message);
			this.open(this.reopenWith);
		});
	}

	markAsCompletedButton() {
		this.disabled = true;

		const data = Mangler.clone(this.record);
		data.accepted_date = MySQLDateToISOPipe.dateToString(data.accepted_date);

		this.api.quote.markSalesOrderAsCompleted(data, () => {
			this.disabled = false;
			this.app.quotePriceChange.emit();
			this.app.quoteStageChange.emit('complete');
			this.modal.close(true);
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	issueDetails(type) {
		this.type = type;
		const list = this.issues.warnings[type];
		if (!list?.length) return;

		if (type === 'insufficient_stock') {
			this.issueTitle = 'Products with insufficient stock';
			this.viewStockModal.open();
			return;
		}

		switch (type) {
			case 'zero_cost': this.issueTitle = 'Items with zero cost'; break;
			case 'zero_price': this.issueTitle = 'Items with zero or negative price'; break;
			case 'zero_margin': this.issueTitle = 'Items with zero margin'; break;
			case 'negative_margin': this.issueTitle = 'Items with negative margin'; break;
			case 'special_order_items': this.issueTitle = 'Special order items'; break;
			case 'negative_labour_hour_margin': this.issueTitle = 'Labour hours mismatch'; break;
			default: this.issueTitle = 'Items'; break;
		};

		this.data = null;
		this.api.quote.lines({ id: this.record.id, line_list: list }, data => {
			this.data = data;

			// Put areas into floors
			const floorIndex = {};
			this.data.floors = [];
			this.data.structure.forEach(s => {
				if (s.type === 'floor') {
					s.areas = [];
					this.data.floors.push(s);
					floorIndex[s.id] = s;
				}
			});
			this.data.structure.forEach(s => {
				if (s.type === 'area') {
					const floor = floorIndex[s.parent_id];
					if (floor) floor.areas.push(s);
				}
			});

			// Add categories to category groups
			this.data.category_groups.forEach(cg => {
				cg.total = 0;
				cg.categories = Mangler.find(this.data.categories, { category_group_id: cg.id });
			});

			this.viewLinesModal.open();
		}, error => {
			this.app.notifications.showDanger(error.message);
		});
	}

	issueList() {
		const list = [];
		Mangler.each(this.issues.warnings, (k, v) => {
			if (this.lineIssues.includes(k) && v.length) list.push(k);
		});
		return list;
	}

	isConfirmed() {
		if (!this.issues) return;
		let confirmed = true;
		Mangler.each(this.confirmed, (_k, v) => { if (!v) confirmed = false; });
		return confirmed;
	}

	isDiscontinued(id) {
		return id && this.data && this.data.discontinued.indexOf(id) !== -1;
	}

	itemHasInvoiced(item) { return item.amount_invoiced > 0; }
	itemInvoicedAmount(item) { return item.amount_invoiced; }
	itemTotalPrice(item) { return item.unit_price * item.quantity * item.quantity_multiplier; }

	itemInvoicedPercentage(item) {
		let percentage = 0;
		const invoiced = this.itemInvoicedAmount(item);
		const total = this.itemTotalPrice(item);
		if (total > 0) percentage = (invoiced / total) * 100;
		return percentage;
	}

	itemHasLabourInvoiced(item) { return item.labour_amount_invoiced > 0; }
	itemLabourInvoicedAmount(item) { return item.labour_amount_invoiced; }
	itemLabourTotalPrice(item) { return item.unit_labour_price * item.quantity * item.quantity_multiplier; }

	itemLabourInvoicedPercentage(item) {
		let percentage = 0;
		const invoiced = this.itemLabourInvoicedAmount(item);
		const total = this.itemLabourTotalPrice(item);
		if (total > 0) percentage = (invoiced / total) * 100;
		return percentage;
	}

}
